import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Plane, X, RotateCcw, Info, ChevronDown } from "lucide-react";

/**
 * VAULT Airport Mode — Full-screen boarding-pass display.
 *
 * Spec-critical rules honoured here:
 *   §1  — Only renders the ACTUAL uploaded/airline-supplied credential.
 *          Never synthesises a barcode from PNR / flight / name.
 *   §4  — Attachment is rendered at native aspect ratio, no overlays.
 *   §6  — VAULT stores static uploads only. `dynamic=false` is stamped;
 *          we never claim a rotating credential.
 *   §11 — Screen Wake Lock while mode is open + brightness hint.
 *   §12 — Wake Lock is released on unmount / background.
 *   §13 — No financial / net-worth / card / balance data on screen.
 *   §20/§21 — Passenger + Segment selectors when the booking has more
 *          than one credential.
 *   §23 — If no credential exists, show fallback CTAs (not a broken UI).
 *   §24 — White background, large route + gate/seat, minimal branding.
 */

export default function AirportMode() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [ctx, setCtx] = useState(null);              // airport-context payload
    const [error, setError] = useState("");
    const [activeId, setActiveId] = useState(id);
    const [attachment, setAttachment] = useState(null); // { b64, type, name }
    const [attErr, setAttErr] = useState("");
    const [picker, setPicker] = useState(null);        // 'passenger' | 'flight' | null
    const wakeRef = useRef(null);

    // ── Fetch context (once per pass) ────────────────────────
    useEffect(() => {
        setCtx(null); setError("");
        api.get(`/travel/boarding-passes/${id}/airport-context`)
            .then((r) => { setCtx(r.data); setActiveId(id); })
            .catch(() => setError("We couldn't load this boarding pass."));
    }, [id]);

    // ── Fetch the attachment for the active pass ─────────────
    const active = useMemo(() => {
        if (!ctx) return null;
        return (ctx.related || []).find((p) => p.id === activeId) || ctx.primary || null;
    }, [ctx, activeId]);

    useEffect(() => {
        setAttachment(null); setAttErr("");
        if (!active || !active.has_attachment) return;
        api.get(`/travel/boarding-passes/${active.id}/attachment`)
            .then((r) => setAttachment(r.data))
            .catch(() => setAttErr("Boarding pass couldn't be displayed."));
    }, [active?.id]);

    // ── Screen Wake Lock (§11-12) + fullscreen body class ────
    useEffect(() => {
        document.body.classList.add("airport-mode-body");
        let cancelled = false;
        (async () => {
            try {
                if ("wakeLock" in navigator) {
                    const wl = await navigator.wakeLock.request("screen");
                    if (!cancelled) wakeRef.current = wl;
                }
            } catch {
                /* Wake Lock not permitted / not supported — silently continue. */
            }
        })();
        const onVis = () => {
            if (document.visibilityState !== "visible" && wakeRef.current) {
                wakeRef.current.release?.().catch(() => {});
                wakeRef.current = null;
            }
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            cancelled = true;
            document.body.classList.remove("airport-mode-body");
            document.removeEventListener("visibilitychange", onVis);
            if (wakeRef.current) wakeRef.current.release?.().catch(() => {});
            wakeRef.current = null;
        };
    }, []);

    const exit = () => navigate(-1);

    // ── Guard states ────────────────────────────────────────
    if (error) {
        return (
            <FallbackFrame title="Airport Mode" onExit={exit}
                message="We couldn't load this boarding pass."
                actions={[
                    { label: "Try Again", onClick: () => window.location.reload(), primary: true, testId: "airport-retry" },
                    { label: "Back to Travel", onClick: () => navigate("/organise/travel"), testId: "airport-back" },
                ]}
            />
        );
    }
    if (!ctx || !active) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center text-stone-500 text-[13px]" data-testid="airport-loading">
                Loading Airport Mode…
            </div>
        );
    }

    // ── If no legitimate credential exists, show fallback (§23) ─
    if (!active.has_attachment) {
        return (
            <FallbackFrame title="Airport Mode" onExit={exit}
                message="Boarding pass couldn't be displayed. This can happen when check-in isn't completed or the airline hasn't issued a boarding credential yet."
                actions={[
                    { label: "Open Airline / Wallet Pass", onClick: () => window.open(`https://www.google.com/search?q=${encodeURIComponent(active.airline + ' web check-in')}`, "_blank"), primary: true, testId: "airport-open-airline" },
                    { label: "Back to Travel", onClick: () => navigate("/organise/travel"), testId: "airport-back" },
                ]}
            />
        );
    }

    // ── Passenger + Segment options (§20, §21) ──────────────
    const passengers = uniquePassengers(ctx.related);
    const segments   = ctx.related || [active];

    return (
        <div className="min-h-screen bg-white text-stone-900 flex flex-col" data-testid="airport-mode">
            {/* Top strip — minimal branding, no finance data */}
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
                <Plane size={16} className="text-amber-600" />
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500">Airport Mode</div>
                <button
                    onClick={exit}
                    className="ml-auto rounded-full p-1.5 hover:bg-stone-100"
                    aria-label="Close Airport Mode"
                    data-testid="airport-close"
                >
                    <X size={16} />
                </button>
            </div>

            <div className="flex-1 flex flex-col items-center px-4 py-5 max-w-3xl mx-auto w-full">
                {/* Flight header — big + scannable */}
                <div className="w-full mb-4">
                    <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">
                        {active.airline || "Airline"} · {active.flight_number || ""}
                    </div>
                    <div className="text-4xl sm:text-5xl font-semibold tabular-nums mt-1" data-testid="airport-route">
                        {active.origin || "—"} <span className="text-stone-400 mx-2">→</span> {active.destination || "—"}
                    </div>
                    <div className="text-[13px] text-stone-500 mt-1">
                        {active.travel_date || ""}
                        {active.departure_time ? ` · Departs ${active.departure_time}` : ""}
                    </div>
                </div>

                {/* Passenger / segment selectors */}
                {(passengers.length > 1 || segments.length > 1) && (
                    <div className="w-full flex items-center gap-2 mb-4">
                        {passengers.length > 1 && (
                            <SelectorButton
                                label="Passenger"
                                value={active.passenger_name || "Select"}
                                onClick={() => setPicker(picker === "passenger" ? null : "passenger")}
                                testId="airport-passenger-picker"
                            />
                        )}
                        {segments.length > 1 && (
                            <SelectorButton
                                label="Flight"
                                value={`${active.origin} → ${active.destination}`}
                                onClick={() => setPicker(picker === "flight" ? null : "flight")}
                                testId="airport-flight-picker"
                            />
                        )}
                    </div>
                )}

                {picker && (
                    <div className="w-full mb-4 rounded-2xl border border-stone-200 divide-y divide-stone-100 bg-white shadow-sm" data-testid={`airport-picker-${picker}`}>
                        {(picker === "passenger" ? passengers : segments).map((opt) => (
                            <button
                                key={opt.id || opt.value}
                                onClick={() => {
                                    if (picker === "passenger") {
                                        const first = (ctx.related || []).find((p) => p.passenger_name === opt.value);
                                        if (first) setActiveId(first.id);
                                    } else {
                                        setActiveId(opt.id);
                                    }
                                    setPicker(null);
                                }}
                                className="w-full text-left px-4 py-3 hover:bg-stone-50 text-[13.5px] text-stone-800"
                                data-testid={`airport-picker-opt-${opt.id || opt.value}`}
                            >
                                {picker === "passenger"
                                    ? opt.value
                                    : `${opt.origin} → ${opt.destination}  ·  ${opt.airline || ""} ${opt.flight_number || ""}`}
                            </button>
                        ))}
                    </div>
                )}

                {/* Gate / Seat / Boarding — large */}
                <div className="w-full grid grid-cols-3 gap-3 mb-5">
                    <MetaLarge label="Boarding" value={active.boarding_time || active.departure_time || "—"} testId="airport-boarding" />
                    <MetaLarge label="Gate" value={active.gate || "—"} testId="airport-gate" />
                    <MetaLarge label="Seat" value={active.seat || "—"} testId="airport-seat" />
                </div>

                {/* THE credential — original file, no overlay, high resolution */}
                <div
                    className="w-full max-w-2xl bg-white rounded-xl border border-stone-200 p-3 sm:p-4 shadow-sm"
                    role="figure"
                    aria-label={`Boarding pass barcode for ${active.airline || "flight"} ${active.flight_number || ""}`}
                    data-testid="airport-credential"
                >
                    {!attachment && !attErr && (
                        <div className="text-center py-16 text-stone-400 text-[13px]">Loading credential…</div>
                    )}
                    {attErr && (
                        <div className="text-center py-10 text-rose-700 text-[13px]" data-testid="airport-credential-error">
                            {attErr}
                        </div>
                    )}
                    {attachment && attachment.attachment_type === "pdf" && (
                        <embed
                            src={`data:application/pdf;base64,${attachment.attachment_b64}`}
                            type="application/pdf"
                            className="w-full block"
                            style={{ height: "68vh" }}
                            data-testid="airport-credential-pdf"
                        />
                    )}
                    {attachment && attachment.attachment_type !== "pdf" && (
                        <img
                            src={`data:image/${attachment.attachment_type};base64,${attachment.attachment_b64}`}
                            alt="Boarding pass"
                            className="w-full h-auto block"
                            style={{ imageRendering: "auto" }}
                            data-testid="airport-credential-img"
                        />
                    )}
                </div>

                {/* Status strip */}
                <div className="w-full mt-4 flex items-center gap-2 text-[12px] text-stone-500">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full font-semibold ${
                        ctx.dynamic ? "text-rose-800 bg-rose-50 border border-rose-100"
                                    : "text-emerald-800 bg-emerald-50 border border-emerald-100"
                    }`}
                    data-testid="airport-credential-status">
                        {ctx.dynamic ? "Live credential — internet required" : "Boarding Pass Ready · Available offline"}
                    </span>
                    <span className="hidden sm:inline">
                        {ctx.dynamic
                            ? "Open the airline or Wallet boarding pass for the latest credential."
                            : "For easier scanning, increase screen brightness."}
                    </span>
                </div>

                {/* Refresh / secondary actions */}
                <div className="w-full mt-4 flex items-center gap-2 justify-end">
                    <button
                        onClick={async () => {
                            try {
                                await api.post(`/travel/pnr/${active.id}/refresh`);
                                window.location.reload();
                            } catch {
                                /* Non-fatal — refresh may be provider-limited. */
                            }
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[12.5px] font-semibold text-stone-800"
                        data-testid="airport-refresh"
                    >
                        <RotateCcw size={12} /> Refresh Flight
                    </button>
                    <button
                        onClick={() => navigate(`/organise/travel`)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[12.5px] font-semibold text-stone-800"
                        data-testid="airport-flight-details"
                    >
                        Flight Details
                    </button>
                </div>
            </div>
        </div>
    );
}

function MetaLarge({ label, value, testId }) {
    return (
        <div className="rounded-xl bg-stone-50 border border-stone-200 px-3 py-3 text-center" data-testid={testId}>
            <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{label}</div>
            <div className="text-2xl sm:text-3xl font-semibold text-stone-900 tabular-nums mt-1">{value}</div>
        </div>
    );
}

function SelectorButton({ label, value, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[12.5px] text-stone-800"
        >
            <span className="font-semibold">{label}</span>
            <span className="text-stone-500">{value}</span>
            <ChevronDown size={12} />
        </button>
    );
}

function uniquePassengers(rows) {
    const seen = new Map();
    (rows || []).forEach((p) => {
        const name = (p.passenger_name || "").trim();
        if (name && !seen.has(name)) seen.set(name, { value: name });
    });
    return Array.from(seen.values());
}

function FallbackFrame({ title, message, actions, onExit }) {
    return (
        <div className="min-h-screen bg-white flex flex-col" data-testid="airport-fallback">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center gap-2">
                <Plane size={16} className="text-amber-600" />
                <div className="text-[11px] font-bold uppercase tracking-widest text-stone-500">{title}</div>
                <button
                    onClick={onExit}
                    className="ml-auto rounded-full p-1.5 hover:bg-stone-100"
                    aria-label="Close Airport Mode"
                    data-testid="airport-close"
                >
                    <X size={16} />
                </button>
            </div>
            <div className="flex-1 flex items-center justify-center p-6">
                <div className="max-w-md w-full rounded-2xl border border-stone-200 p-6 text-center">
                    <div className="w-12 h-12 rounded-full bg-stone-100 flex items-center justify-center mx-auto mb-3">
                        <Info size={17} className="text-stone-500" />
                    </div>
                    <p className="text-[13.5px] text-stone-700 mb-4">{message}</p>
                    <div className="flex flex-col gap-2">
                        {actions.map((a) => (
                            <button
                                key={a.label}
                                onClick={a.onClick}
                                data-testid={a.testId}
                                className={
                                    a.primary
                                        ? "px-4 py-2 rounded-full bg-amber-400 hover:bg-amber-300 text-stone-900 text-[13px] font-semibold"
                                        : "px-4 py-2 rounded-full border border-stone-300 hover:border-stone-500 text-[13px] font-semibold text-stone-800"
                                }
                            >
                                {a.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
